import type { PopperPlacementType } from "@mui/material";
import { TOUR, TourTarget } from "./tourTargets";

/**
 * Contextual hints: one-off coach marks that arrive when a feature
 * first becomes relevant, rather than in a burst at minute zero.
 *
 * Each is a plain anchored Popper, deliberately *not* a one-step
 * joyride tour — a hint that fires on canvas-open would pull the tour's
 * 76 kB chunk far more often than "first-run only" (see the lazy
 * boundary note in Tour.tsx).
 *
 * Each hint carries its own key and version in the per-user onboarding
 * map, so dismissing one never touches the tour or another hint, and a
 * reworked hint can be re-shown by bumping just its version.
 */
export interface HintConditions {
  baseView: "reading" | "canvas";
  rectangleCount: number;
  evaluationMode: boolean;
}

export interface HintDef {
  key: string;
  version: number;
  title: string;
  body: string;
  /** Anchor. The hint only shows while this element is on screen. */
  target: TourTarget;
  placement?: PopperPlacementType;
  /** Additional gate beyond the anchor being present. */
  when?: (conditions: HintConditions) => boolean;
}

export const hints: HintDef[] = [
  {
    key: "hint:categories",
    version: 1,
    title: "Categories carry the argument",
    body:
      "These aren't just colours. Setting, Reasoning Step and Target are " +
      "the parts of an argument — pick the role this passage plays, and " +
      "evaluation mode can later check your diagram against it. Question " +
      "is for your own marginalia.",
    target: TOUR.categoryPicker,
    placement: "right-start",
    when: ({ baseView }) => baseView === "reading",
  },
  {
    // Version 2: the original copy announced "two nodes on the canvas —
    // now show how they relate", which reads oddly on the seeded sample
    // where nodes are already connected, and described the wrong
    // gesture. Bumped so anyone who saw v1 gets the corrected one.
    key: "hint:arrows",
    version: 2,
    title: "Connecting nodes",
    body:
      "Drag one node on top of another to link them — it springs back and " +
      "a line appears between them. Right-click that line to give it a " +
      "direction, label it with the inference it stands for, or say how " +
      "strongly you hold it.",
    target: TOUR.canvasSurface,
    placement: "top",
    when: ({ baseView, rectangleCount }) =>
      baseView === "canvas" && rectangleCount >= 2,
  },
  {
    key: "hint:evaluation",
    version: 1,
    title: "What evaluation mode checks",
    body:
      "It reads the arrows as a directed graph and flags what breaks an " +
      "argument: circular reasoning, steps nothing supports, and a missing " +
      "setting node. Issues appear as a badge up here — they're prompts to " +
      "think again, not errors.",
    target: TOUR.evaluationToggle,
    placement: "bottom-end",
    when: ({ baseView, evaluationMode }) =>
      baseView === "canvas" && evaluationMode,
  },
];
